import billingPolicy from './config/billingPolicy.json';
/**
 * Account.js — User account page.
 *
 * Sections:
 *   - Profile       live (firstName, lastName, degree, pronouns on users/{uid})
 *   - Invitations   existing invitation management for eligible users
 *   - Settings      opens the existing Settings modal when enabled
 *   - Billing       shown only while billing is enabled
 *
 * Reachable from the AppShell user menu and via /account.
 * /settings redirects here (Settings is now modal-only).
 *
 * Profile writes use the same rules path as self-serve user updates:
 * firestore.rules `allow update: if request.auth.uid == userId`.
 *
 * See: docs/updates/archive/2026-04-20-user-profile-fields-for-patient-sharing.md
 */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "./utils/firebase";
import { useAuth } from "./utils/authContext";
import { showNotification } from "./utils/notificationSystem";
import {
  DEGREE_OPTIONS,
  PRONOUN_OPTIONS,
  PRONOUN_LABELS,
  US_STATE_OPTIONS,
  isProfileComplete,
  isValidNpi,
  normalizeLicenses,
  readLicensesFromUserData,
} from "./utils/userProfile";
import Settings from "./Settings";
import InvitationsSection from "./components/InvitationsSection";
import { meetsMinRole } from "./utils/roleUtils";
import { useFeatureFlags } from "./contexts/FeatureFlagContext";
import { companyName, versionNum } from "./globals";
import "./css/page-account.css";

const Account = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;
  const { flags } = useFeatureFlags();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [degree, setDegree] = useState("");
  const [degreeFreeText, setDegreeFreeText] = useState("");
  const [pronouns, setPronouns] = useState("");
  const [npi, setNpi] = useState("");
  const [licenses, setLicenses] = useState([]);

  const [isSettingsOpen, setIsSettingsOpen] = useState(
    searchParams.get("settings") === "1"
  );

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", userId));
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : {};
        setUserData(data);
        setFirstName(data.firstName || "");
        setLastName(data.lastName || "");

        // Degree: if stored value is in the enum, use it; else treat as "Other" + free text
        const storedDegree = (data.degree || "").trim();
        if (!storedDegree) {
          setDegree("");
          setDegreeFreeText("");
        } else if (DEGREE_OPTIONS.includes(storedDegree)) {
          setDegree(storedDegree);
          setDegreeFreeText("");
        } else {
          setDegree("Other");
          setDegreeFreeText(storedDegree);
        }

        // Pronouns — simple enum match. Any legacy free-text value falls
        // back to empty "Select Pronouns" rather than showing a stale option.
        const storedPronouns = (data.pronouns || "").trim();
        setPronouns(PRONOUN_OPTIONS.includes(storedPronouns) ? storedPronouns : "");

        // Optional credential fields (NPI + state license).
        setNpi((data.npi || "").trim());
        setLicenses(readLicensesFromUserData(data));
      } catch (err) {
        console.error("[Account] Failed to load user doc:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleSave = useCallback(async () => {
    if (!userId) return;
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    // Resolve degree — "Other" stores the free-text value directly
    let resolvedDegree = "";
    if (degree === "Other") resolvedDegree = degreeFreeText.trim();
    else resolvedDegree = degree.trim();

    // Pronouns — stored as-is from the enum
    const resolvedPronouns = pronouns.trim();

    // Optional credentials — stored as trimmed strings.
    const resolvedNpi = npi.trim();
    const resolvedLicenses = normalizeLicenses(licenses);

    if (!isValidNpi(resolvedNpi)) {
      showNotification("NPI must be exactly 10 digits", "error", 3500);
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, "users", userId), {
        firstName: trimmedFirst,
        lastName: trimmedLast,
        degree: resolvedDegree,
        pronouns: resolvedPronouns,
        npi: resolvedNpi,
        licenses: resolvedLicenses,
      });
      setUserData((prev) => ({
        ...(prev || {}),
        firstName: trimmedFirst,
        lastName: trimmedLast,
        degree: resolvedDegree,
        pronouns: resolvedPronouns,
        npi: resolvedNpi,
        licenses: resolvedLicenses,
      }));
      showNotification("Profile saved", "info", 2500);
    } catch (err) {
      console.error("[Account] Save failed:", err);
      showNotification("Failed to save profile", "error", 4000);
    } finally {
      setSaving(false);
    }
  }, [userId, firstName, lastName, degree, degreeFreeText, pronouns, npi, licenses]);

  const openSettings = useCallback(() => {
    setIsSettingsOpen(true);
    // Reflect in URL so /settings redirect → /account?settings=1 auto-opens
    setSearchParams({ settings: "1" }, { replace: true });
  }, [setSearchParams]);

  const closeSettings = useCallback(() => {
    setIsSettingsOpen(false);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Debug Mode — super-admin-only toggle on users/{uid}.settings.debugMode.
  // See: docs/features/admin/debug-mode.md (and the `debugMode` flag in featureFlagDefinitions.js).
  const debugModeOn = !!userData?.settings?.debugMode;
  const handleToggleDebugMode = useCallback(async () => {
    if (!userId) return;
    const next = !debugModeOn;
    try {
      await updateDoc(doc(db, "users", userId), {
        "settings.debugMode": next,
      });
      setUserData((prev) => ({
        ...(prev || {}),
        settings: { ...((prev && prev.settings) || {}), debugMode: next },
      }));
      showNotification(`Debug Mode ${next ? "ON" : "OFF"}`, "info", 2000);
    } catch (err) {
      console.error("[Account] Failed to toggle debugMode:", err);
      showNotification("Failed to toggle Debug Mode", "error", 4000);
    }
  }, [userId, debugModeOn]);

  const complete = isProfileComplete(userData);

  if (loading) {
    return (
      <div className="page-container account-page">
        <div className="header-container">
          <Tooltip title="Go back" arrow>
            <button className="back-button" onClick={() => navigate(-1)}>&larr;</button>
          </Tooltip>
          <div className="page-title">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container account-page">
      <div className="header-container account-header">
        <div className="account-header-inner">
          <div className="account-heading">
            <Tooltip title="Go back" arrow>
              <button className="back-button" onClick={() => navigate(-1)}>&larr;</button>
            </Tooltip>
            <h1 className="page-title">My Account</h1>
          </div>
          <div className="account-header-actions">
            {userData?.userRole === "superAdmin" && (
              <Tooltip
                title={debugModeOn
                  ? "Debug Mode is ON — dev-only UI is visible. Click to turn off."
                  : "Debug Mode is OFF — click to turn on dev-only UI (e.g. recording-quality dots, internal IDs)."}
                arrow
              >
                <button type="button"
                  className={`button-secondary${debugModeOn ? ' account-debug-active' : ''}`}
                  onClick={handleToggleDebugMode}>
                  Debug Mode: {debugModeOn ? "ON" : "OFF"}
                </button>
              </Tooltip>
            )}
            {flags?.userSettingsMenu && (
              <button type="button" className="button-secondary" onClick={openSettings}>
                My Settings
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="account-content">
        <section className="account-card" aria-labelledby="account-profile-heading">
          <div className="account-card-body">
            <h2 id="account-profile-heading" className="account-section-title">Profile</h2>
            {!complete && (
              <p className="account-profile-notice">
                Please add your first and last name so your notes can be attributed correctly.
              </p>
            )}
            <div className="account-field-grid">
              <div className="account-field">
                <label htmlFor="account-first-name">First name *</label>
                <input id="account-first-name" type="text" value={firstName}
                  onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" />
              </div>
              <div className="account-field">
                <label htmlFor="account-last-name">Last name *</label>
                <input id="account-last-name" type="text" value={lastName}
                  onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" />
              </div>
            </div>
            <fieldset className="account-pronouns">
              <legend>Pronouns</legend>
              <div className="account-radio-options">
                {PRONOUN_OPTIONS.map((opt) => (
                  <label key={opt}>
                    <input type="radio" name="account-pronouns" value={opt}
                      checked={pronouns === opt} onChange={(e) => setPronouns(e.target.value)} />
                    {PRONOUN_LABELS[opt] || opt}
                  </label>
                ))}
                <label>
                  <input type="radio" name="account-pronouns" value="" checked={pronouns === ""}
                    onChange={() => setPronouns("")} />
                  None
                </label>
              </div>
            </fieldset>

            <div className="account-subsection">
              <h3 className="account-subsection-title">Professional details</h3>
              <div className="account-field-grid">
                <div className="account-field">
                  <label htmlFor="account-degree">Degree</label>
                  <select id="account-degree" value={degree} onChange={(e) => setDegree(e.target.value)}>
                    <option value="">— Select —</option>
                    {DEGREE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  {degree === "Other" && (
                    <input type="text" value={degreeFreeText} onChange={(e) => setDegreeFreeText(e.target.value)}
                      placeholder="Enter your degree" aria-label="Enter degree" />
                  )}
                </div>
                <div className="account-field">
                  <label htmlFor="account-npi">NPI number (optional)</label>
                  <input id="account-npi" type="text" inputMode="numeric" value={npi}
                    onChange={(e) => setNpi(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="10-digit number" autoComplete="off" />
                </div>
              </div>

              <fieldset className="account-licenses">
                <legend>State licenses (optional)</legend>
                {licenses.length === 0 && <p className="account-empty-text">No licenses added.</p>}
                {licenses.map((row, idx) => (
                  <div className="account-license-row" key={idx}>
                    <div className="account-field">
                      <label htmlFor={`account-license-state-${idx}`}>State</label>
                      <select id={`account-license-state-${idx}`} value={row.state || ""}
                        aria-label={`License ${idx + 1} state`}
                        onChange={(e) => {
                          const next = [...licenses];
                          next[idx] = { ...next[idx], state: e.target.value };
                          setLicenses(next);
                        }}>
                        <option value="">— State —</option>
                        {US_STATE_OPTIONS.map((state) => <option key={state.code} value={state.code}>{state.name}</option>)}
                      </select>
                    </div>
                    <div className="account-field">
                      <label htmlFor={`account-license-number-${idx}`}>License number</label>
                      <input id={`account-license-number-${idx}`} type="text" value={row.number || ""}
                        aria-label={`License ${idx + 1} number`} autoComplete="off"
                        onChange={(e) => {
                          const next = [...licenses];
                          next[idx] = { ...next[idx], number: e.target.value };
                          setLicenses(next);
                        }} />
                    </div>
                    <button type="button" className="account-remove-license"
                      onClick={() => setLicenses(licenses.filter((_, i) => i !== idx))}
                      aria-label={`Remove license ${idx + 1}`} title={`Remove license ${idx + 1}`}>
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" className="button-secondary account-add-license"
                  onClick={() => setLicenses([...licenses, { state: "", number: "" }])}>
                  + Add license
                </button>
              </fieldset>
            </div>
          </div>
          <div className="account-card-actions">
            <button type="button" className="button-confirm" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save profile"}
            </button>
          </div>
        </section>

        {meetsMinRole(userData?.userRole || "standard", "standard") && (
          <section className="account-card account-invitations" aria-labelledby="account-invitations-heading">
            <div className="account-card-body">
              <h2 id="account-invitations-heading" className="account-section-title">Invitations</h2>
              <div className="account-invitations-content">
                <InvitationsSection userData={userData} userRole={userData?.userRole || "standard"}
                  onUserDataChange={setUserData} />
              </div>
            </div>
          </section>
        )}

        {billingPolicy.enabled && (
          <section className="account-card">
            <div className="account-card-body">
              <h2 className="account-section-title">Billing</h2>
              <p>Use the "Manage Billing" option in the user menu.</p>
            </div>
          </section>
        )}

        <footer className="account-footer">
          &copy; {new Date().getFullYear()} {companyName} v{versionNum}
          <br />
          All rights reserved.
        </footer>
      </div>

      <Dialog
        open={isSettingsOpen}
        onClose={closeSettings}
        maxWidth={false}
        sx={{
          zIndex: 'var(--z-modal-backdrop)',
          '& .MuiDialog-container': { alignItems: 'flex-start', paddingTop: '72px' },
          // Dialog portals outside AppShell, so Settings needs an in-flow layout here.
          '& .page-container': {
            position: 'relative', top: 'auto', left: 'auto', width: '100%',
            maxWidth: '100%', minHeight: 0, margin: 0, padding: '20px 24px 28px',
          },
          '& .header-container': {
            position: 'relative', top: 'auto', left: 'auto', width: '100%',
            zIndex: 'auto', padding: 0, height: 48, marginBottom: '16px',
          },
        }}
        slotProps={{
          paper: {
            'aria-label': 'Your Featherglass Settings',
            style: {
              background: "var(--color-panel-background, #fff)",
              borderRadius: "8px",
              maxWidth: "960px",
              width: "calc(100% - 48px)",
              margin: 0,
              maxHeight: "calc(100% - 104px)",
              overflowY: "auto",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.25)",
              position: "relative",
              display: "block",
            },
          },
        }}
      >
        <button
          autoFocus
          onClick={closeSettings}
          aria-label="Close settings"
          style={{
            position: "sticky",
            top: "8px",
            float: "right",
            marginRight: "8px",
            background: "transparent",
            border: "none",
            fontSize: "20px",
            color: "var(--color-text-primary, #111827)",
            cursor: "pointer",
            zIndex: 1,
          }}
        >
          ✕
        </button>
        <Settings />
      </Dialog>
    </div>
  );
};

export default Account;
